import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { PriceConstructionTypeRelationStatusEnum, type PriceListUerRowData, ProjectMethodologyEnum } from 'gql/graphql';
import { useContractConstructionTypes } from 'pages/Project/Common/Contract/hooks/useContractConstructionTypes';
import { Button, cnMixSpace, IconArrowLeft, IconArrowRight, IconConnection } from 'uikit';
import { formatNumberToRu } from 'utils/formatNumberToRu';

import type { IHeaderParams, ValueFormatterParams } from 'ag-grid-community';
import React, { useCallback, useMemo, useState } from 'react';

export const listItemsCost = {
    ozp: 'ОЗП',
    emm: 'ЭММ',
    zpm: 'ЗПМ',
    mtr: 'МТР',
    eqp: 'ОБ',
    pir: 'ПИР',
    trn: 'Перевозка',
    nr: 'НР',
    sp: 'СП',
};

export const TotalHeaderComponent = (props: IHeaderParams & { displayName: string; collapseKeys: string[] }) => {
    const { collapseKeys, displayName, api } = props;

    const [isExpanded, setIsExpanded] = useState(false);

    const onExpandCollapseClick = useCallback(() => {
        api.setColumnsVisible(collapseKeys, !isExpanded);
        setIsExpanded(!isExpanded);
    }, [api, collapseKeys, isExpanded]);

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {displayName}
            <Button
                view="clear"
                iconRight={isExpanded ? IconArrowLeft : IconArrowRight}
                onlyIcon
                onClick={onExpandCollapseClick}
                size="xs"
                form="brick"
            />
        </div>
    );
};
TotalHeaderComponent.displayName = 'TotalHeaderComponent';
export const useUERExtraColumns = (contractId: number) => {
    const [contractConstructionTypes] = useContractConstructionTypes(contractId, ProjectMethodologyEnum.Uer);

    const extraColumnNames = useMemo(
        () =>
            contractConstructionTypes?.map((type) => ({
                columnName: type.shortName,
                id: type.id,
            })),
        [contractConstructionTypes],
    );

    const renderColumnCosts = useCallback(
        (objId: number) =>
            Object.entries(listItemsCost).map(([key, value]) => ({
                headerName: value,
                field: `${objId}.${key}`,
                hide: true,
                width: 120,
                valueFormatter: (params: ValueFormatterParams<PriceListUerRowData>) => {
                    const { data } = params;

                    const currentObj = data?.currentPrices?.find((price) => price.constructionTypeId === objId);
                    const currentValue = currentObj?.[key as keyof typeof currentObj];

                    if (!currentValue) {
                        return '';
                    }

                    return formatNumberToRu(currentValue, 2);
                },
            })),
        [],
    );

    return useMemo(() => {
        const otherCostTableExtColumns = extraColumnNames?.map(({ columnName, id }) => ({
            headerName: columnName,
            field: columnName,
            marryChildren: true,
            children: [
                {
                    headerName: 'Итого',
                    headerComponent: TotalHeaderComponent,
                    headerComponentParams: {
                        collapseKeys: Object.keys(listItemsCost).map((k) => `${id}.${k}`),
                    },
                    field: `${columnName}_total`,
                    cellRenderer: ({ data }: { data: PriceListUerRowData }) => {
                        if (!data) {
                            return '';
                        }

                        const currentTotalObj = data?.currentPrices?.find((price) => price.constructionTypeId === id);

                        const { unitCostWithOther, constructionTypeRelationStatus } = currentTotalObj ?? {};

                        if (!unitCostWithOther) {
                            return '';
                        }

                        return (
                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                                {constructionTypeRelationStatus ===
                                    PriceConstructionTypeRelationStatusEnum.NewRecord && (
                                    <ComponentWithTooltip tooltipText="Новая связь">
                                        <IconConnection size="s" view="link" className={cnMixSpace({ mR: 'xs' })} />
                                    </ComponentWithTooltip>
                                )}
                                {formatNumberToRu(unitCostWithOther, 2)}
                            </div>
                        );
                    },
                },
                ...renderColumnCosts(id),
            ],
        }));

        return {
            otherCostTableExtColumns: [
                {
                    headerName:
                        'Стоимость единицы УЕР в текущем уровне цен (с учетом прочих и лимитированных затрат), руб.без НДС',
                    children: otherCostTableExtColumns,
                },
            ],
        };
    }, [extraColumnNames, renderColumnCosts]);
};
